import types from '../mutation-types';
import FollowUpFlowsAPI from '../../api/followUpFlows';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
};

export const getters = {
  getFlows(_state) {
    return _state.records;
  },
  getFlow: _state => flowId => {
    return _state.records.find(record => record.id === Number(flowId));
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isFetching: true });
    try {
      const response = await FollowUpFlowsAPI.getFlows();
      commit(types.SET_FOLLOW_UP_FLOWS, response.data);
      return response.data;
    } finally {
      commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isFetching: false });
    }
  },
  create: async ({ commit }, flowData) => {
    commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isCreating: true });
    try {
      const response = await FollowUpFlowsAPI.createFlow(flowData);
      commit(types.ADD_FOLLOW_UP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { flowId, flowData }) => {
    commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await FollowUpFlowsAPI.updateFlow(flowId, flowData);
      commit(types.EDIT_FOLLOW_UP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: false });
    }
  },
  activate: async ({ commit }, flowId) => {
    commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await FollowUpFlowsAPI.activateFlow(flowId);
      commit(types.EDIT_FOLLOW_UP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: false });
    }
  },
  // A single contact answers synchronously with its results; a batch answers
  // 202 with the id of a durable background run. Neither touches the flows
  // collection — the outcome goes straight back to the caller.
  enrollContacts: async (_context, { flowId, contactIds }) => {
    const response = await FollowUpFlowsAPI.enrollContacts(flowId, contactIds);
    return response.data;
  },
  fetchEnrollmentRun: async (_context, { flowId, runId, after }) => {
    const response = await FollowUpFlowsAPI.getEnrollmentRun(flowId, runId, {
      after,
    });
    return response.data;
  },
  fetchEnrollmentRuns: async (_context, flowId) => {
    const response = await FollowUpFlowsAPI.getEnrollmentRuns(flowId);
    return response.data.runs;
  },
  pause: async ({ commit }, flowId) => {
    commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await FollowUpFlowsAPI.pauseFlow(flowId);
      commit(types.EDIT_FOLLOW_UP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: false });
    }
  },
  resume: async ({ commit }, flowId) => {
    commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await FollowUpFlowsAPI.resumeFlow(flowId);
      commit(types.EDIT_FOLLOW_UP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: false });
    }
  },
  archive: async ({ commit }, flowId) => {
    commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await FollowUpFlowsAPI.archiveFlow(flowId);
      commit(types.EDIT_FOLLOW_UP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, flowId) => {
    commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: true });
    try {
      await FollowUpFlowsAPI.deleteFlow(flowId);
      commit(types.DELETE_FOLLOW_UP_FLOW, flowId);
    } finally {
      commit(types.SET_FOLLOW_UP_FLOW_UI_FLAG, { isUpdating: false });
    }
  },
};

export const mutations = {
  [types.SET_FOLLOW_UP_FLOW_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_FOLLOW_UP_FLOWS]: MutationHelpers.set,
  [types.ADD_FOLLOW_UP_FLOW]: MutationHelpers.create,
  [types.EDIT_FOLLOW_UP_FLOW]: MutationHelpers.update,
  [types.DELETE_FOLLOW_UP_FLOW]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
